export interface ContentItem {
  id: string;
  title: string;
  type: 'VIDEO' | 'SCORM' | 'PDF' | 'LINK';
  description: string;
  author: string;
  updated: string;
  size: string;
  status: 'Active' | 'Draft' | 'Archived';
  tags: string[];
}

export interface RecentUpload {
  id: string;
  title: string;
  type: 'VIDEO' | 'SCORM' | 'PDF' | 'LINK';
  uploadedDate: string;
}

export interface Category {
  id: string;
  name: string;
}

export interface ContentDetails {
  id: string;
  title: string;
  type: string;
  status: string;
  description: string;
  category: string;
  language: string;
  tags: string[];
  owner: string;
  fileSize: string;
  createdDate: string;
  lastUpdated: string;
  filename: string;
  usedIn: string[];
  stats: {
    totalViews: number;
    downloads: number;
    usedInCourses: number;
    versions: number;
  };
  versions: {
    version: string;
    current: boolean;
    uploadedBy: string;
    date: string;
    size: string;
    notes: string;
  }[];
}

export const getLibraryContent = async (): Promise<{
  success: boolean;
  data?: ContentItem[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: ContentItem[] = [
      {
        id: '1',
        title: 'Introduction to Leadership',
        type: 'VIDEO',
        description: 'A comprehensive video course covering fundamental leadership principles',
        author: 'Sarah Mitchell',
        updated: 'Updated 2 days ago',
        size: '256 MB',
        status: 'Active',
        tags: ['leadership', 'management', 'fundamentals']
      },
      {
        id: '2',
        title: 'Data Analytics SCORM Package',
        type: 'SCORM',
        description: 'Interactive SCORM course for data analytics fundamentals',
        author: 'Mike Chen',
        updated: 'Updated 5 days ago',
        size: '128 MB',
        status: 'Active',
        tags: ['analytics', 'data', 'scorm']
      },
      {
        id: '3',
        title: 'Project Management Guide',
        type: 'PDF',
        description: 'Comprehensive PDF guide for project management best practices',
        author: 'Emily Rodriguez',
        updated: 'Updated 1 week ago',
        size: '12 MB',
        status: 'Active',
        tags: ['project management', 'guide', 'best practices']
      },
      {
        id: '4',
        title: 'Communication Skills Workshop',
        type: 'LINK',
        description: 'External video content from industry experts',
        author: 'James Wilson',
        updated: 'Updated 3 days ago',
        size: '-',
        status: 'Active',
        tags: ['communication', 'external', 'workshop']
      },
      {
        id: '5',
        title: 'Cybersecurity Basics',
        type: 'VIDEO',
        description: 'Draft content for upcoming security training',
        author: 'Lisa Anderson',
        updated: 'Updated 1 day ago',
        size: '180 MB',
        status: 'Draft',
        tags: ['security', 'cybersecurity', 'draft']
      },
      {
        id: '6',
        title: 'Archived Sales Training',
        type: 'PDF',
        description: 'Old sales training material - archived',
        author: 'Robert Taylor',
        updated: 'Updated 3 months ago',
        size: '8 MB',
        status: 'Archived',
        tags: ['sales', 'archived']
      }
    ];

    await new Promise(resolve => setTimeout(resolve, 800));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<ContentItem[]>(ENDPOINTS.LIBRARY_CONTENT);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch library content' }
    };
  }
};

export const getRecentUploads = async (): Promise<{
  success: boolean;
  data?: RecentUpload[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: RecentUpload[] = [
      {
        id: '1',
        title: 'Introduction to Leadership',
        type: 'VIDEO',
        uploadedDate: '2 days ago'
      },
      {
        id: '2',
        title: 'Data Analytics SCORM Package',
        type: 'SCORM',
        uploadedDate: '5 days ago'
      },
      {
        id: '3',
        title: 'Project Management Guide',
        type: 'PDF',
        uploadedDate: '1 week ago'
      }
    ];

    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<RecentUpload[]>(ENDPOINTS.RECENT_UPLOADS);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch recent uploads' }
    };
  }
};

export const getCategories = async (): Promise<{
  success: boolean;
  data?: Category[];
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    const response: Category[] = [
      { id: '1', name: 'Compliance' },
      { id: '2', name: 'Customer Service' },
      { id: '3', name: 'Marketing' },
      { id: '4', name: 'Sales' },
      { id: '5', name: 'SoftSkills' }
    ];

    await new Promise(resolve => setTimeout(resolve, 300));
    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<Category[]>(ENDPOINTS.CATEGORIES);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch categories' }
    };
  }
};

export const getContentDetails = async (contentId: string): Promise<{
  success: boolean;
  data?: ContentDetails;
  error?: { message: string } | null;
}> => {
  try {
    // ===== USING MOCK DATA =====
    // Simulating fetching details for specific content
    const mockDetails: Record<string, ContentDetails> = {
      '1': {
        id: '1',
        title: 'Introduction to Leadership',
        type: 'VIDEO',
        status: 'Active',
        description: 'A comprehensive video course covering fundamental leadership principles',
        category: 'Leadership',
        language: 'English',
        tags: ['leadership', 'management', 'fundamentals'],
        owner: 'Sarah Mitchell',
        fileSize: '256 MB',
        createdDate: 'Jan 15, 2024',
        lastUpdated: '2 days ago',
        filename: 'leadership_intro.mp4',
        usedIn: ['Leadership Development Program', 'Manager Training Track'],
        stats: {
          totalViews: 1234,
          downloads: 567,
          usedInCourses: 2,
          versions: 2
        },
        versions: [
          {
            version: '2.0',
            current: true,
            uploadedBy: 'Sarah Mitchell',
            date: 'Nov 20, 2024',
            size: '256 MB',
            notes: 'Updated content with new examples'
          },
          {
            version: '1.0',
            current: false,
            uploadedBy: 'Sarah Mitchell',
            date: 'Jan 15, 2024',
            size: '248 MB',
            notes: 'Initial version'
          }
        ]
      },
      '2': {
        id: '2',
        title: 'Data Analytics SCORM Package',
        type: 'SCORM',
        status: 'Active',
        description: 'Interactive SCORM course for data analytics fundamentals',
        category: 'Data Science',
        language: 'English',
        tags: ['analytics', 'data', 'scorm'],
        owner: 'Mike Chen',
        fileSize: '128 MB',
        createdDate: 'Feb 10, 2024',
        lastUpdated: '5 days ago',
        filename: 'data_analytics.zip',
        usedIn: ['Data Science Bootcamp', 'Analytics Fundamentals'],
        stats: {
          totalViews: 856,
          downloads: 342,
          usedInCourses: 2,
          versions: 1
        },
        versions: [
          {
            version: '1.0',
            current: true,
            uploadedBy: 'Mike Chen',
            date: 'Feb 10, 2024',
            size: '128 MB',
            notes: 'Initial SCORM package release'
          }
        ]
      }
    };

    await new Promise(resolve => setTimeout(resolve, 600));

    const response = mockDetails[contentId];
    if (!response) {
      return {
        success: false,
        error: { message: 'Content not found' }
      };
    }

    return {
      success: true,
      data: response,
      error: null
    };

    // ===== API IMPLEMENTATION (Currently commented out) =====
    // const response = await httpClient.get<ContentDetails>(`${ENDPOINTS.CONTENT_DETAILS}/${contentId}`);
    // return { success: true, data: response, error: null };

  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch content details' }
    };
  }
};
